import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Pause, Play, RotateCw, X, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Dialog, DialogContent } from './ui/dialog';
import {
  createEmptyBoard,
  randomTetromino,
  rotateTetromino,
  isValidPosition,
  mergePieceToBoard,
  clearLines,
  calculateScore,
  getGameSpeed,
  isGameOver,
} from '../utils/tetrisLogic';
import { getVersesForBook, getVersesForChapter } from '../data/bibleBooks';
import {
  getHighScore,
  setHighScore,
  addCollectedVerse,
  removeCollectedVersesByBook,
  removeCollectedVersesByChapter,
  updateBookProgress,
  updateStatistics,
  getStatistics,
  markBookAsCompleted,
  getCompletedBooksCount,
  getBookProgress,
  saveGameState,
  loadGameState,
} from '../utils/localStorage';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../utils/auth';
import { toast } from 'sonner';
import { useGameToasts } from '../hooks/useGameToasts';
import { useToast } from '../context/ToastContext';
import { ProgressCard } from './ProgressCard';
import soundEffects from '../utils/soundEffects';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;

// DAS (Delayed Auto Shift) Constants
const DAS_DELAY = 200;
const DAS_INTERVAL = 75;
const SOFT_DROP_INTERVAL = 50;
const HARD_DROP_HOLD_THRESHOLD = 500; // Time in ms to trigger hard drop from hold

export const GamePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedBook = location.state?.selectedBook;
  const { toastChapterRevealed, toastBookCompleted, toastAchievementUnlocked } = useGameToasts();
  const { addToast } = useToast();
  const resumeGame = location.state?.resumeGame;

  // Game state
  const [board, setBoard] = useState(createEmptyBoard(BOARD_HEIGHT, BOARD_WIDTH));
  const [currentPiece, setCurrentPiece] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [nextPiece, setNextPiece] = useState(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScoreState] = useState(getHighScore());

  // Verse state
  const [bookData, setBookData] = useState({ totalChapters: 0, chapters: {}, allVerses: [] });
  const [currentChapter, setCurrentChapter] = useState(1);
  const [currentVerseIndexInChapter, setCurrentVerseIndexInChapter] = useState(0);
  const [completedChapters, setCompletedChapters] = useState(0);
  const [revealedVerses, setRevealedVerses] = useState([]);
  const [showVerseModal, setShowVerseModal] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [pendingVerses, setPendingVerses] = useState([]);
  const [recentVerses, setRecentVerses] = useState([]);
  const [shouldHardDrop, setShouldHardDrop] = useState(false);
  const [totalVersesRevealed, setTotalVersesRevealed] = useState(0);
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('tetris_sound_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const gameLoopRef = useRef(null);
  const moveIntervalRef = useRef(null);
  const moveDelayTimerRef = useRef(null);
  const keyPressedRef = useRef({});
  const downKeyTimerRef = useRef(null);
  const freeFallIntervalRef = useRef(null);
  const autoSaveRef = useRef(null);
  const isExplicitExitRef = useRef(false);

  // Sync refs with state to prevent stale closures in intervals
  const boardRef = useRef(board);
  const currentPieceRef = useRef(currentPiece);
  const positionRef = useRef(position);
  const isPausedRef = useRef(isPaused);
  const gameOverRef = useRef(gameOver);

  useEffect(() => { boardRef.current = board; }, [board]);
  useEffect(() => { currentPieceRef.current = currentPiece; }, [currentPiece]);
  useEffect(() => { positionRef.current = position; }, [position]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);
  useEffect(() => { gameOverRef.current = gameOver; }, [gameOver]);

  // Initialize verses
  useEffect(() => {
    if (selectedBook) {
      const data = getVersesForBook(selectedBook.id);
      setBookData(data);

      // Check if we should resume a saved game
      if (resumeGame) {
        const savedProgress = getBookProgress(selectedBook.id);
        if (savedProgress && savedProgress.isStarted) {
          // Restore progress
          setCurrentChapter(savedProgress.currentChapter || 1);
          setCurrentVerseIndexInChapter(savedProgress.currentVerseInChapter || 0);
          setCompletedChapters(savedProgress.chaptersCompleted || 0);
          setTotalVersesRevealed(savedProgress.totalVersesRevealed || 0);
          setScore(savedProgress.score || 0);
          setLines(savedProgress.lines || 0);
          setLevel(savedProgress.level || 1);
        } else {
          // Find first chapter that has verses
          const firstChapter = Object.keys(data.chapters).sort((a, b) => parseInt(a) - parseInt(b))[0];
          setCurrentChapter(parseInt(firstChapter) || 1);
        }
      } else {
        // Find first chapter that has verses
        const firstChapter = Object.keys(data.chapters).sort((a, b) => parseInt(a) - parseInt(b))[0];
        setCurrentChapter(parseInt(firstChapter) || 1);
      }
    } else {
      // No book selected, go back
      navigate('/select-book');
    }
  }, [selectedBook, navigate, resumeGame]);

  // Save progress function
  // Save progress and sync with Supabase
  const syncStatsToSupabase = useCallback(async (stats) => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) return;

      const { error } = await supabase
        .from('profiles')
        .update({
          high_score: stats.highScore,
          total_score: stats.totalScore,
          total_verses_revealed: stats.totalVerses,
          games_played: stats.gamesPlayed,
          books_completed: stats.booksCompleted,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error syncing stats to Supabase:', error);
    }
  }, []);

  const saveProgress = useCallback(() => {
    if (!selectedBook || !gameStarted) return;

    // Calculate percentage based on total verses
    const totalVersesInBook = bookData.allVerses.length;
    const percentage = totalVersesInBook > 0
      ? Math.round((totalVersesRevealed / totalVersesInBook) * 100)
      : 0;

    updateBookProgress(selectedBook.id, {
      currentChapter,
      currentVerseInChapter: currentVerseIndexInChapter,
      chaptersCompleted: completedChapters,
      totalVersesRevealed,
      percentage: Math.min(percentage, 100),
      score: isExplicitExitRef.current ? 0 : score,
      lines: isExplicitExitRef.current ? 0 : lines,
      level: isExplicitExitRef.current ? 1 : level,
    });

    // Also sync global stats to Supabase periodically
    const currentStats = getStatistics();
    const currentHighScore = getHighScore();
    const currentCompletedBooks = getCompletedBooksCount();

    syncStatsToSupabase({
      highScore: Math.max(currentHighScore, score),
      totalScore: currentStats.totalScore, // currentStats already includes previous sessions
      totalVerses: currentStats.totalVersesRevealed,
      gamesPlayed: currentStats.gamesPlayed,
      booksCompleted: currentCompletedBooks
    });
  }, [selectedBook, gameStarted, currentChapter, currentVerseIndexInChapter, completedChapters, totalVersesRevealed, score, lines, level, bookData, syncStatsToSupabase]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (gameStarted && !gameOver) {
      autoSaveRef.current = setInterval(() => {
        saveProgress();
      }, 30000);
    }

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
    };
  }, [gameStarted, gameOver, saveProgress]);

  // Save progress when game is paused or when leaving
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveProgress();
      soundEffects.stopAll();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPaused(true);
        soundEffects.suspend();
        saveProgress();
      } else {
        // When coming back, we stay paused to avoid surprises
        // but we resume the context so sounds are ready
        soundEffects.resume();
      }
    };

    const handleBlur = () => {
      setIsPaused(true);
      soundEffects.suspend();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);

      // CRITICAL: Stop all sounds and intervals when leaving the page
      soundEffects.stopAll();
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
      if (moveIntervalRef.current) clearInterval(moveIntervalRef.current);
      if (freeFallIntervalRef.current) clearInterval(freeFallIntervalRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);

      // Save when component unmounts
      if (gameStarted && !gameOver) {
        saveProgress();
      }
    };
  }, [saveProgress, gameStarted, gameOver]);

  // Save when paused
  useEffect(() => {
    if (isPaused && gameStarted) {
      saveProgress();
      soundEffects.suspend();
    } else if (!isPaused && gameStarted && !gameOver) {
      soundEffects.resume();
    }
  }, [isPaused, gameStarted, gameOver, saveProgress]);


  // Initialize game
  const initGame = useCallback(() => {
    const piece = randomTetromino();
    const next = randomTetromino();
    setCurrentPiece(piece);
    setNextPiece(next);
    setPosition({ x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 });
    setBoard(createEmptyBoard(BOARD_HEIGHT, BOARD_WIDTH));

    // If resuming, keep the loaded values; otherwise reset
    if (!resumeGame) {
      setScore(0);
      setLines(0);
      setLevel(1);
      const firstChapter = Object.keys(bookData.chapters).sort((a, b) => parseInt(a) - parseInt(b))[0];
      setCurrentChapter(parseInt(firstChapter) || 1);
      setCurrentVerseIndexInChapter(0);
      setCompletedChapters(0);
      setTotalVersesRevealed(0);
    }

    setGameStarted(true);
    setGameOver(false);
    setRevealedVerses([]);
    setRecentVerses([]);
    setIsPaused(false);
    setShowGameOverModal(false);
  }, [bookData, resumeGame]);

  // Restart game after loss:
  // - Keeps verses from previously COMPLETED chapters
  // - Removes only the verses from the CURRENT unfinished chapter
  // - Resets score to 0 for the new attempt
  const handleRestartAfterGameOver = useCallback(() => {
    if (!selectedBook) return;

    // 1. Remove only the current chapter's uncompleted verses from collection
    removeCollectedVersesByChapter(selectedBook.id, currentChapter);

    // 2. Recalculate: totalVersesRevealed should only subtract current chapter partial progress
    const lostVersesCount = currentVerseIndexInChapter;
    setCurrentVerseIndexInChapter(0);
    setTotalVersesRevealed(prev => Math.max(0, prev - lostVersesCount));

    // 3. Close modal and reset game state
    setShowGameOverModal(false);
    setGameOver(false);
    setScore(0);
    setLines(0);
    setLevel(1);

    // 4. Spawn new pieces and start fresh
    const piece = randomTetromino();
    const next = randomTetromino();
    setCurrentPiece(piece);
    setNextPiece(next);
    setPosition({ x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 });
    setBoard(createEmptyBoard(BOARD_HEIGHT, BOARD_WIDTH));
    setRevealedVerses([]);
    setRecentVerses([]);
    setIsPaused(false);
    setGameStarted(true);
  }, [selectedBook, currentChapter, currentVerseIndexInChapter]);

  // Move piece
  const movePiece = useCallback(
    (dx, dy) => {
      const curPiece = currentPieceRef.current;
      const curPos = positionRef.current;
      const curBoard = boardRef.current;
      
      if (!curPiece || isPausedRef.current || gameOverRef.current) return false;

      const newPosition = { x: curPos.x + dx, y: curPos.y + dy };
      if (isValidPosition(curBoard, curPiece.shape, newPosition)) {
        setPosition(newPosition);
        // PositionRef is updated in useEffect, but we update it here too for immediate use in same cycle
        positionRef.current = newPosition; 
        
        // Play move sound for horizontal movement or soft drop
        if (dx !== 0 || dy !== 0) {
          soundEffects.move();
        }
        return true;
      }
      return false;
    },
    [] // Stable reference
  );

  const stopMoving = useCallback(() => {
    if (moveDelayTimerRef.current) {
      clearTimeout(moveDelayTimerRef.current);
      moveDelayTimerRef.current = null;
    }
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
      moveIntervalRef.current = null;
    }
  }, []);

  const stopSoftDrop = useCallback(() => {
    if (downKeyTimerRef.current) {
      clearTimeout(downKeyTimerRef.current);
      downKeyTimerRef.current = null;
    }
    if (freeFallIntervalRef.current) {
      clearInterval(freeFallIntervalRef.current);
      freeFallIntervalRef.current = null;
    }
  }, []);

  const performHardDrop = useCallback(() => {
    const curPiece = currentPieceRef.current;
    const curPos = positionRef.current;
    const curBoard = boardRef.current;
    
    if (!curPiece || isPausedRef.current || gameOverRef.current) return;

    let newY = curPos.y;
    // Find the lowest valid position
    while (isValidPosition(curBoard, curPiece.shape, { x: curPos.x, y: newY + 1 })) {
      newY++;
    }

    stopMoving();
    stopSoftDrop();
    soundEffects.hardDrop();
    setPosition({ x: curPos.x, y: newY });
    positionRef.current = { x: curPos.x, y: newY };
    setShouldHardDrop(true);
  }, [stopMoving, stopSoftDrop]);

  const startMoving = useCallback((dx, dy) => {
    if (isPaused || gameOver || showVerseModal) return;
    stopMoving();
    movePiece(dx, dy);
    moveDelayTimerRef.current = setTimeout(() => {
      moveIntervalRef.current = setInterval(() => {
        movePiece(dx, dy);
      }, DAS_INTERVAL);
    }, DAS_DELAY);
  }, [movePiece, isPaused, gameOver, showVerseModal, stopMoving]);

  const startSoftDrop = useCallback(() => {
    if (isPausedRef.current || gameOverRef.current || showVerseModal) return;
    
    // Evitar acumulaciones redundantes de timers
    stopSoftDrop();

    // Primer movimiento inmediato para feedback instantáneo
    movePiece(0, 1);
    
    // Mantenida = Bajada constante "nivel por nivel" de forma ultra-rápida
    freeFallIntervalRef.current = setInterval(() => {
      const moved = movePiece(0, 1);
      if (!moved) {
        setShouldHardDrop(true);
      }
    }, 25); // 25ms para que se sienta que "vuela" hacia abajo

    // Hard Drop automático al sostener por un tiempo corto (180ms)
    downKeyTimerRef.current = setTimeout(() => {
      performHardDrop();
    }, 180);
  }, [movePiece, showVerseModal, stopSoftDrop, performHardDrop]);

  // Rotate piece
  const handleRotate = useCallback(() => {
    if (!currentPiece || isPaused || gameOver) return;

    const rotated = rotateTetromino(currentPiece.shape);
    if (isValidPosition(board, rotated, position)) {
      stopMoving();
      // Rotation also clears DAS timers as per requirements
      setCurrentPiece({ ...currentPiece, shape: rotated });
      soundEffects.rotate();
    }
  }, [currentPiece, board, position, isPaused, gameOver]);

  // Lock piece and spawn new one
  const lockPiece = useCallback(() => {
    if (!currentPiece) return;

    // Play lock sound
    soundEffects.lock();

    // Merge piece to board
    const newBoard = mergePieceToBoard(board, currentPiece.shape, position, currentPiece.color);

    // Check for game over
    const spawnPos = { x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 };
    if (isGameOver(newBoard, nextPiece.shape, spawnPos)) {
      setBoard(newBoard);
      setGameOver(true);
      setGameStarted(false);
      setShowGameOverModal(true); // Mostrar el nuevo modal

      // Play game over sound
      soundEffects.gameOver();

      // Update statistics (Only register game played; score/verses are saved upon chapter completion)
      updateStatistics({
        gamesPlayed: 1,
      });

      // Sync global stats to Supabase
      const currentStats = getStatistics();
      const currentHighScore = getHighScore();
      const currentCompletedBooks = getCompletedBooksCount();

      syncStatsToSupabase({
        highScore: currentHighScore,
        totalScore: currentStats.totalScore,
        totalVerses: currentStats.totalVersesRevealed,
        gamesPlayed: currentStats.gamesPlayed,
        booksCompleted: currentCompletedBooks
      });

      // Check high score
      const isNewHighScore = setHighScore(score);
      if (isNewHighScore) {
        setHighScoreState(score);
        toastAchievementUnlocked('Nuevo Récord', score, `¡Puntuación más alta: ${score}!`);
      }

      return;
    }

    // Clear lines
    const { newBoard: clearedBoard, linesCleared } = clearLines(newBoard);
    setBoard(clearedBoard);

    // Update score and lines
    if (linesCleared > 0) {
      // Play line clear sound
      soundEffects.lineClear(linesCleared);

      const points = calculateScore(linesCleared);
      setScore((prev) => prev + points);
      setLines((prev) => prev + linesCleared);

      // Level up every 10 lines
      const newTotalLines = lines + linesCleared;
      const newLevel = Math.floor(newTotalLines / 10) + 1;
      if (newLevel > level) {
        setLevel(newLevel);
        soundEffects.levelUp();
        addToast(`¡Dificultad aumentada!`, 'success');
      }

      // Reveal verses based on lines cleared
      const versesToReveal = [];
      let tempChapter = currentChapter;
      let tempVerseIndex = currentVerseIndexInChapter;
      let completedChaptersList = [];
      let lastChapterTransitioned = null;

      for (let i = 0; i < linesCleared; i++) {
        const chapterVerses = bookData.chapters[tempChapter] || [];

        if (tempVerseIndex < chapterVerses.length) {
          const verse = chapterVerses[tempVerseIndex];
          versesToReveal.push(verse);
          addCollectedVerse(verse);
          tempVerseIndex++;

          // Check if chapter is completed
          if (tempVerseIndex >= chapterVerses.length) {
            completedChaptersList.push(tempChapter);

            // Move to next chapter if available
            const chapterNumbers = Object.keys(bookData.chapters)
              .map(n => parseInt(n))
              .sort((a, b) => a - b);
            const currentChapterIndex = chapterNumbers.indexOf(tempChapter);

            if (currentChapterIndex < chapterNumbers.length - 1) {
              lastChapterTransitioned = chapterNumbers[currentChapterIndex + 1];
              tempChapter = lastChapterTransitioned;
              tempVerseIndex = 0;
            } else {
              // Book completed
              break;
            }
          }
        }
      }

      if (versesToReveal.length > 0) {
        setPendingVerses(versesToReveal);
        setShowVerseModal(true);
        setIsPaused(true);

        // Update state
        setCurrentVerseIndexInChapter(tempVerseIndex);
        setCurrentChapter(tempChapter); // This will update the background UI
        setRevealedVerses((prev) => [...prev, ...versesToReveal]);

        // Update total verses revealed
        const newTotalVersesRevealed = totalVersesRevealed + versesToReveal.length;
        setTotalVersesRevealed(newTotalVersesRevealed);

        // Process completed chapters
        let newCompletedChaptersCount = completedChapters;
        let newlyEarnedGlobalVerses = 0;

        completedChaptersList.forEach(chNum => {
          newCompletedChaptersCount++;
          const chVersesCount = bookData.chapters[chNum] ? bookData.chapters[chNum].length : 0;
          newlyEarnedGlobalVerses += chVersesCount;
          toastChapterRevealed(selectedBook.name, chNum, '¡Capítulo completado!');
        });
        setCompletedChapters(newCompletedChaptersCount);

        // SAVE TO GLOBAL STATS ONLY IF CHAPTERS WERE COMPLETED
        if (completedChaptersList.length > 0) {
          updateStatistics({
            totalScore: newlyEarnedGlobalVerses * 10,
            totalLines: newlyEarnedGlobalVerses,
            totalVersesRevealed: newlyEarnedGlobalVerses,
          });
        }

        // If book completed
        const lastChapterInBook = Object.keys(bookData.chapters)
          .map(n => parseInt(n))
          .sort((a, b) => b - a)[0];

        if (completedChaptersList.includes(lastChapterInBook)) {
          const isNewCompletion = markBookAsCompleted(selectedBook.id);
          if (isNewCompletion) {
            // Clear verses of this book from the collection as requested
            removeCollectedVersesByBook(selectedBook.id);

            toastBookCompleted(selectedBook.name, score);
          }
        }

        // Update book progress
        const totalVersesInBook = bookData.allVerses.length;
        const percentage = totalVersesInBook > 0
          ? Math.round((newTotalVersesRevealed / totalVersesInBook) * 100)
          : 0;

        updateBookProgress(selectedBook.id, {
          currentChapter: tempChapter,
          currentVerseInChapter: tempVerseIndex,
          chaptersCompleted: newCompletedChaptersCount,
          totalVersesRevealed: newTotalVersesRevealed,
          percentage: Math.min(percentage, 100),
          score: score + points,
          lines: lines + linesCleared,
          level: newLevel > level ? newLevel : level,
        });

        // Sync global stats to Supabase accurately
        const stats = getStatistics();
        const currentHighScore = getHighScore();
        const currentCompletedBooks = getCompletedBooksCount();

        syncStatsToSupabase({
          highScore: Math.max(currentHighScore, score + points),
          totalScore: stats.totalScore, // Uses accurate globally saved score
          totalVerses: stats.totalVersesRevealed, // Uses accurate globally saved verses
          gamesPlayed: stats.gamesPlayed,
          booksCompleted: currentCompletedBooks
        });

        // Update recent verses (max 5)
        setRecentVerses((prev) => {
          const updated = [...versesToReveal.slice().reverse(), ...prev];
          return updated.slice(0, 5);
        });
      }

    }

    // Spawn next piece
    setCurrentPiece(nextPiece);
    setNextPiece(randomTetromino());
    setPosition({ x: Math.floor(BOARD_WIDTH / 2) - 1, y: 0 });
    setShouldHardDrop(false);
    
    // Si el jugador sigue sosteniendo la tecla hacia abajo tras spawnear, re-activamos el soft drop
    if (keyPressedRef.current['ArrowDown']) {
      // Pequeño delay para que no sea injusto
      setTimeout(() => {
        if (keyPressedRef.current['ArrowDown']) {
          startSoftDrop();
        }
      }, 100);
    }
  }, [nextPiece, board, position, score, lines, level, bookData, currentChapter, currentVerseIndexInChapter, completedChapters, revealedVerses, selectedBook, totalVersesRevealed, startSoftDrop]);

  // Game loop
  useEffect(() => {
    if (!gameStarted || isPaused || gameOver) {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
      return;
    }

    const speed = getGameSpeed(level);
    gameLoopRef.current = setInterval(() => {
      const moved = movePiece(0, 1);
      if (!moved) {
        lockPiece();
      }
    }, speed);

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameStarted, isPaused, gameOver, level, movePiece, lockPiece]);

  // Keyboard controls with hard drop on hold
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver || showVerseModal) return;

      // Prevent default for arrow keys
      if (e.key.startsWith('Arrow') || e.key === ' ') {
        e.preventDefault();
      }

      // Track key state
      if (keyPressedRef.current[e.key]) return; // Already pressed
      keyPressedRef.current[e.key] = true;

      switch (e.key) {
        case 'ArrowLeft':
          startMoving(-1, 0);
          break;

        case 'ArrowRight':
          startMoving(1, 0);
          break;

        case 'ArrowDown':
          startSoftDrop();
          break;

        case 'ArrowUp':
          handleRotate();
          break;

        case ' ':
          // Space bar = Hard drop (instant drop)
          performHardDrop();
          break;

        case 'p':
        case 'P':
          setIsPaused((prev) => !prev);
          break;

        default:
          break;
      }
    };

    const handleKeyUp = (e) => {
      keyPressedRef.current[e.key] = false;

      // Stop continuous movement when key is released
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        stopMoving();
      }

      // Handle ArrowDown release to prevent hard drop if it was just a quick tap
      if (e.key === 'ArrowDown') {
        stopSoftDrop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      stopMoving();
      stopSoftDrop();
    };
  }, [startMoving, stopMoving, startSoftDrop, stopSoftDrop, handleRotate, performHardDrop, gameOver, showVerseModal]);

  // Start game on mount — only auto-start on first load, NEVER after game over
  useEffect(() => {
    if (bookData.totalChapters > 0 && !gameStarted && !gameOver) {
      initGame();
    }
  }, [bookData, gameStarted, gameOver, initGame]);

  // Handle hard drop - lock piece immediately after position update
  useEffect(() => {
    if (shouldHardDrop && currentPiece) {
      setShouldHardDrop(false);
      // Wait a frame for position to update visually
      setTimeout(() => {
        lockPiece();
      }, 50);
    }
  }, [shouldHardDrop, currentPiece, lockPiece]);

  // Close verse modal
  const handleCloseVerseModal = () => {
    setShowVerseModal(false);
    setPendingVerses([]);
    setIsPaused(false);
  };

  // Handle explicit exit via back button
  const handleBackButtonClick = useCallback(() => {
    if (window.confirm('¿Salir del juego? Se perderá el progreso de los puntos actuales.')) {
      isExplicitExitRef.current = true;

      // Update global statistics in local storage
      // Add current session's progress to the absolute totals
      const sessionVersesCount = revealedVerses.length;
      updateStatistics({
        totalScore: score,
        totalVersesRevealed: sessionVersesCount,
        gamesPlayed: (score > 0 || sessionVersesCount > 0) ? 1 : 0
      });

      // The unmount cleanup will call saveProgress() which now knows 
      // it's an explicit exit and will reset the book-specific score to 0.
      navigate('/');
    }
  }, [navigate, score, revealedVerses.length, updateStatistics]);

  // Render board with current piece
  const renderBoard = () => {
    const displayBoard = board.map((row) => [...row]);

    // Draw current piece on board
    if (currentPiece && !gameOver) {
      currentPiece.shape.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell) {
            const y = position.y + rowIndex;
            const x = position.x + colIndex;
            if (y >= 0 && y < BOARD_HEIGHT && x >= 0 && x < BOARD_WIDTH) {
              displayBoard[y][x] = currentPiece.color;
            }
          }
        });
      });
    }

    return displayBoard;
  };

  const displayBoard = renderBoard();
  const totalVersesInBook = bookData.allVerses.length;
  const totalVersesInChapter = bookData.chapters[currentChapter]?.length || 0;
  const bookProgressPercent = totalVersesInBook > 0
    ? (totalVersesRevealed / totalVersesInBook) * 100
    : 0;

  if (!selectedBook) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 pb-6 text-foreground">
      {/* Header */}
      <header className="bg-card/80 glass border-b border-border sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackButtonClick}
              className="p-2 hover:bg-secondary rounded-full interactive"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>

            {/* Book and Chapter Info */}
            <div className="text-center flex-1">
              <div className="text-lg font-bold text-foreground">{selectedBook.name}</div>
              <div className="text-sm text-muted-foreground">Capítulo {currentChapter}</div>
            </div>

            <div className="w-10"></div> {/* Spacer for symmetry */}
          </div>

          {/* Chapter Progress */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Progreso del Libro</span>
              <span>{Math.round(bookProgressPercent)}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${bookProgressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>
      </header>

      {/* Game Area */}
      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* MOBILE VIEW */}
        <div className="lg:hidden">
          <div className="game-board-container flex gap-3 items-stretch justify-center">

            <div className="game-sidebar w-24 flex-shrink-0">
              <ProgressCard title="Nivel">{getCompletedBooksCount()}</ProgressCard>
              <ProgressCard title="Puntos">{score}</ProgressCard>
              <ProgressCard title="Capítulos">{currentChapter}/{bookData.totalChapters}</ProgressCard>
              <ProgressCard title="Versículos">{currentVerseIndexInChapter}/{totalVersesInChapter}</ProgressCard>
              <ProgressCard title="Siguiente">
                <div className="flex justify-center items-center h-10">
                  {nextPiece && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${nextPiece.shape[0].length}, 6px)`,
                        gap: '1px',
                      }}
                    >
                      {nextPiece.shape.map((row, rowIndex) =>
                        row.map((cell, colIndex) => (
                          <div
                            key={`${rowIndex}-${colIndex}`}
                            style={{
                              width: '6px',
                              height: '6px',
                              backgroundColor: cell ? nextPiece.color : 'transparent',
                            }}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              </ProgressCard>
              <ProgressCard
                title={isPaused ? 'Reanudar' : 'Pausar'}
                onClick={() => setIsPaused(!isPaused)}
                icon={isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              />
              <ProgressCard
                title="Sonido"
                onClick={() => {
                  const newValue = !isSoundEnabled;
                  setIsSoundEnabled(newValue);
                  localStorage.setItem('tetris_sound_enabled', JSON.stringify(newValue));
                  soundEffects.setEnabled(newValue);
                }}
                icon={isSoundEnabled ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              />
            </div>

            {/* Tetris Board */}
            <div className="tetris-game-box flex-1 max-w-[300px]">
              <div
                className="tetris-grid p-1 relative bg-white/90 rounded-2xl w-full h-full"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${BOARD_WIDTH}, 1fr)`,
                  gap: '2px',
                }}
              >
                {displayBoard.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className="tetromino-block"
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        backgroundColor: cell || 'hsl(var(--muted))',
                        opacity: cell ? 1 : 0.3,
                      }}
                    />
                  ))
                )}

                {/* Overlays */}
                {(isPaused && !gameOver) && (
                  <div className="absolute inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center rounded-2xl z-20">
                    <div className="text-center px-4">
                      <Pause className="w-12 h-12 text-primary mx-auto mb-2" />
                      <p className="text-xl font-black">PAUSADO</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Controls below sidebar+board */}
          <div className="flex justify-center mt-6">
            <div className="w-full flex grid grid-cols-4 gap-3 max-w-[400px]">
              <button
                onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = true; startMoving(-1, 0); }}
                onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = false; stopMoving(); }}
                onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = false; stopMoving(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                className="bg-white hover:bg-secondary rounded-2xl border-2 border-border interactive h-14 flex items-center justify-center shadow-sm"
              >
                <span className="text-2xl font-bold">←</span>
              </button>
              <button
                onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = true; startSoftDrop(); }}
                onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = false; stopSoftDrop(); }}
                onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = false; stopSoftDrop(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                className="bg-primary/20 hover:bg-primary/30 rounded-2xl border-2 border-primary/40 interactive h-14 flex items-center justify-center shadow-sm"
              >
                <span className="text-2xl font-bold">↓</span>
              </button>
              <button
                onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = true; startMoving(1, 0); }}
                onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = false; stopMoving(); }}
                onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = false; stopMoving(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                className="bg-white hover:bg-secondary rounded-2xl border-2 border-border interactive h-14 flex items-center justify-center shadow-sm"
              >
                <span className="text-2xl font-bold">→</span>
              </button>
              <button
                onPointerDown={(e) => { e.preventDefault(); handleRotate(); }}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                className="bg-yellow-400 hover:bg-yellow-500 text-black rounded-2xl border-2 border-yellow-500 interactive h-14 flex items-center justify-center shadow-lg"
              >
                <RotateCw className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Recent Verse Mobile */}
          {recentVerses.length > 0 && (
            <div className="mt-6 flex justify-center">
              <Card className="w-full border-2 border-primary/5 rounded-2xl p-4 shadow-sm relative overflow-hidden max-w-[400px]">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-yellow-400"></div>
                <div className="pl-2">
                  <span className="text-[10px] font-black uppercase text-muted-foreground/60 tracking-wider">Último versículo</span>
                  <p className="text-xs font-medium italic leading-tight mt-1">"{recentVerses[0].text}"</p>
                  <p className="text-[10px] font-black text-primary mt-1">{recentVerses[0].reference}</p>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* DESKTOP VIEW */}
        <div className="hidden lg:flex flex-col items-center gap-8">

          {/* Main Game Row */}
          <div className="flex justify-center items-stretch gap-6 w-full max-w-6xl">

            {/* Desktop Sidebar Left */}
            <div className="game-sidebar w-40 flex-shrink-0">
              <ProgressCard title="Nivel">{getCompletedBooksCount()}</ProgressCard>
              <ProgressCard title="Puntos">{score}</ProgressCard>
              <ProgressCard title="Capítulos">{currentChapter}/{bookData.totalChapters}</ProgressCard>
              <ProgressCard title="Versículos">{currentVerseIndexInChapter}/{totalVersesInChapter}</ProgressCard>
              <ProgressCard title="Siguiente">
                <div className="flex justify-center items-center h-12">
                  {nextPiece && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${nextPiece.shape[0].length}, 8px)`,
                        gap: '1.5px',
                      }}
                    >
                      {nextPiece.shape.map((row, rowIndex) =>
                        row.map((cell, colIndex) => (
                          <div
                            key={`${rowIndex}-${colIndex}`}
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: cell ? nextPiece.color : 'transparent',
                            }}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              </ProgressCard>
              <ProgressCard
                title={isPaused ? 'Reanudar' : 'Pausar'}
                onClick={() => setIsPaused(!isPaused)}
                icon={isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              />
              <ProgressCard
                title="Sonido"
                onClick={() => {
                  const newValue = !isSoundEnabled;
                  setIsSoundEnabled(newValue);
                  localStorage.setItem('tetris_sound_enabled', JSON.stringify(newValue));
                  soundEffects.setEnabled(newValue);
                }}
                icon={isSoundEnabled ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              />
            </div>

            {/* Desktop Tetris Board */}
            <div className="tetris-game-box shadow-2xl border-8 border-primary/5 rounded-[32px] overflow-hidden flex-shrink-0 h-fit">
              <div
                className="tetris-grid p-2 relative bg-white/95"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${BOARD_WIDTH}, 32px)`,
                  gridTemplateRows: `repeat(${BOARD_HEIGHT}, 32px)`,
                  gap: '2px',
                }}
              >
                {displayBoard.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className="tetromino-block rounded-sm transition-all duration-200"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: cell || 'hsl(var(--muted))',
                        opacity: cell ? 1 : 0.2,
                      }}
                    />
                  ))
                )}

                {/* Overlays Desktop */}
                {(isPaused && !gameOver) && (
                  <div className="absolute inset-0 bg-background/90 backdrop-blur-md flex items-center justify-center z-50">
                    <div className="text-center p-8 bg-card rounded-3xl shadow-2xl border-2 border-primary/20 max-w-xs">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Pause className="w-8 h-8 text-primary" />
                      </div>
                      <h2 className="text-2xl font-black mb-2">PAUSADO</h2>
                      <Button onClick={() => setIsPaused(false)} className="w-full bg-primary font-bold h-12 rounded-xl mt-4">CONTINUAR</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Sidebar Right: Verses */}
            <div className="game-sidebar w-72 flex-shrink-0">
              <Card className="h-full bg-card/40 backdrop-blur-sm border-2 border-primary/5 rounded-[32px] overflow-hidden flex flex-col shadow-xl">
                <div className="p-5 bg-primary/5 border-b border-primary/10 flex-shrink-0">
                  <h3 className="text-xs font-black text-primary uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    Versículos
                  </h3>
                </div>
                <div className="flex-1 p-5 space-y-4 overflow-y-auto custom-scrollbar">
                  {recentVerses.map((verse, index) => (
                    <div key={index} className="p-4 bg-white rounded-2xl border border-primary/5 shadow-sm hover:shadow-md transition-all group">
                      <p className="text-xs leading-relaxed italic text-foreground/80 mb-2">"{verse.text}"</p>
                      <p className="text-[10px] font-black text-primary text-right">— {verse.reference}</p>
                    </div>
                  ))}
                  {recentVerses.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-40 px-6">
                      <div className="text-4xl mb-4">📖</div>
                      <p className="text-xs font-bold uppercase tracking-widest leading-loose">Completa líneas para revelar versículos</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Desktop Controls below everything */}
          <div className="flex justify-center gap-6 w-full">
            <button
              onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = true; startMoving(-1, 0); }}
              onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = false; stopMoving(); }}
              onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowLeft'] = false; stopMoving(); }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none', userSelect: 'none' }}
              className="w-16 h-16 bg-primary/20 hover:bg-primary/30 rounded-2xl border-2 border-primary/40 shadow-lg text-2xl font-black"
            >
              ←
            </button>

            <button
              onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = true; startSoftDrop(); }}
              onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = false; stopSoftDrop(); }}
              onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowDown'] = false; stopSoftDrop(); }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none', userSelect: 'none' }}
              className="w-16 h-16 bg-primary/20 hover:bg-primary/30 rounded-2xl border-2 border-primary/40 shadow-lg text-2xl font-black"
            >
              ↓
            </button>

            <button
              onPointerDown={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = true; startMoving(1, 0); }}
              onPointerUp={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = false; stopMoving(); }}
              onPointerLeave={(e) => { e.preventDefault(); keyPressedRef.current['ArrowRight'] = false; stopMoving(); }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none', userSelect: 'none' }}
              className="w-16 h-16 bg-primary/20 hover:bg-primary/30 rounded-2xl border-2 border-primary/40 shadow-lg text-2xl font-black"
            >
              →
            </button>
            <button
              onPointerDown={(e) => { e.preventDefault(); handleRotate(); }}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none', userSelect: 'none' }}
              className="w-16 h-16 bg-yellow-400 hover:bg-yellow-500 text-black rounded-2xl border-2 border-yellow-500 shadow-xl flex items-center justify-center"
            >
              <RotateCw className="w-8 h-8" />
            </button>
          </div>
        </div>

      </main>

      {/* Verse Reveal Modal */}
      <Dialog open={showVerseModal} onOpenChange={setShowVerseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden border-2 border-primary/30 flex flex-col rounded-3xl">
          <div className="bg-card p-6 border-b border-border flex-shrink-0 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
              <span className="text-primary text-xs font-black tracking-widest uppercase">
                ✨ Versículo Revelado
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-card px-8 py-8">
            <div className="space-y-8">
              {pendingVerses.map((verse, index) => (
                <div key={index} className="relative">
                  <div className="absolute -left-4 top-0 bottom-0 w-1 bg-primary/20 rounded-full"></div>
                  <p className="text-xl md:text-2xl font-medium leading-relaxed italic mb-4">"{verse.text}"</p>
                  <p className="text-right font-black text-primary">— {verse.reference}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card p-6 border-t border-border flex-shrink-0 flex justify-center">
            <Button onClick={handleCloseVerseModal} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-12 h-14 font-black shadow-xl">
              CONTINUAR JUGANDO
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Game Over Modal — non-dismissable, requires explicit button press */}
      <Dialog open={showGameOverModal}>
        <DialogContent
          hideClose={true}
          className="max-w-md p-0 overflow-hidden border-2 border-destructive/30 flex flex-col rounded-3xl"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="bg-gradient-to-b from-destructive/20 to-destructive/5 p-8 text-center pt-10">
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 border-4 border-destructive/30 shadow-lg overflow-hidden">
              <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-4xl font-black text-destructive uppercase tracking-tight mb-1">
              GAME OVER
            </h2>
            <p className="text-sm text-muted-foreground">Las fichas llegaron al límite</p>
          </div>

          {/* Score Summary */}
          <div className="px-8 py-6 bg-card space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary/60 rounded-2xl p-4 text-center border border-border">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-wider mb-1">Puntos</p>
                <p className="text-2xl font-black text-foreground">{score.toLocaleString()}</p>
              </div>
              <div className="bg-secondary/60 rounded-2xl p-4 text-center border border-border">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-wider mb-1">Versículos</p>
                <p className="text-2xl font-black text-foreground">{revealedVerses.length}</p>
              </div>
            </div>


            {/* Action Buttons */}
            <div className="grid grid-cols-1 gap-3 pt-2">
              <Button
                onClick={handleRestartAfterGameOver}
                size="lg"
                className="w-full h-14 bg-primary hover:bg-primary/90 text-primary-foreground font-black rounded-2xl shadow-lg transition-all active:scale-95 text-base"
              >
                🔄 REINTENTAR CAPÍTULO {currentChapter}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="lg"
                className="w-full h-12 font-bold rounded-2xl border-2 transition-all active:scale-95"
              >
                Salir al Inicio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};